import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpStatusCode,
  HttpContextToken,
} from '@angular/common/http';

export const IS_RETRIED = new HttpContextToken<boolean>(() => false);
/** Set on a request whose errors the caller shows itself: the interceptor then shows nothing. */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/** No response from the backend: network down, or the proxy could not reach it (seen in the browser as CORS / 502-504). */
const UNREACHABLE_STATUSES: ReadonlySet<number> = new Set([
  0, HttpStatusCode.BadGateway, HttpStatusCode.ServiceUnavailable, HttpStatusCode.GatewayTimeout,
]);
/** One message per burst: a page load fires several calls that fail together. */
const ERROR_TOAST_INTERVAL_MS = 10000;
import {
  AUTH_LOGIN_API,
  AUTH_TOKEN_API,
  CENTRAL_PLATFORM,
  COOKIE_KEYS,
  REFRESH_TOKEN_INVALID_MSG,
  UNAUTHORIZED_OPERATION_MSG,
  HEADER_PLATFORM,
  HEADER_ROLE_ID,
  SKIP_PLATFORM_HEADER,
} from '../constant/constant';
import {
  Observable,
  throwError,
  BehaviorSubject
} from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { LoginService } from './login.service';
import { AuthGuardService } from './auth-guard.service';
import { REQUEST_FAILED_MSG, UNABLE_TO_CONNECT_MSG } from '../constant/responseMessage';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isRefreshing: boolean = false;
  private readonly lastErrorToastAt = new Map<string, number>();

  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );

  constructor(
    private toastr: ToastrService,
    private cookieService: CookieService,
    private loginService: LoginService,
    private authGuard: AuthGuardService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const roleId: string = this.cookieService.get(COOKIE_KEYS.ROLE);

    let headers = req.headers;
    if (!req.context.get(SKIP_PLATFORM_HEADER)) {
      headers = headers.set(HEADER_PLATFORM, CENTRAL_PLATFORM);
    }
    if (roleId) headers = headers.set(HEADER_ROLE_ID, roleId);
    req = req.clone({ headers, withCredentials: true });

    return next.handle(req).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          const showToast = !req.context.get(SKIP_ERROR_TOAST);
          if (this.isUnreachable(error)) {
            console.error(`Backend unreachable (status ${error.status}): ${req.method} ${req.url}`);
            if (showToast) {
              this.showErrorOncePerBurst(UNABLE_TO_CONNECT_MSG);
            }
            return throwError(() => error);
          }

          if (
            error.status === HttpStatusCode.Unauthorized &&
            !req.url.includes(AUTH_LOGIN_API)
          ) {
            if (req.url.includes(AUTH_TOKEN_API) || req.context.get(IS_RETRIED)) {
              console.error(REFRESH_TOKEN_INVALID_MSG);
              this.isRefreshing = false;
              this.logout();
              return throwError(() => error);
            }
            return this.handle401Error(req, next);
          }
          console.error(`Request failed (status ${error.status}): ${req.method} ${req.url}`);
          // every other error is shown here, once per burst; a 401 is the session / login flow, not a toast
          if (showToast && error.status !== HttpStatusCode.Unauthorized) {
            this.showErrorOncePerBurst(error.status === HttpStatusCode.Forbidden
              ? UNAUTHORIZED_OPERATION_MSG
              : error.error?.message || REQUEST_FAILED_MSG);
          }
        }
        return throwError(() => error);
      }),
    );
  }

  /** 0 / 502 / 504 always; 503 only when it is not the backend's own message (e.g. the proxy's HTML page). */
  private isUnreachable(error: HttpErrorResponse): boolean {
    if (!UNREACHABLE_STATUSES.has(error.status)) {
      return false;
    }
    return error.status !== HttpStatusCode.ServiceUnavailable || !error.error?.message;
  }

  private showErrorOncePerBurst(message: string): void {
    const now = Date.now();
    if (now - (this.lastErrorToastAt.get(message) ?? 0) < ERROR_TOAST_INTERVAL_MS) {
      return;
    }
    this.lastErrorToastAt.set(message, now);
    this.toastr.error(message);
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.loginService.getAccessToken().pipe(
        switchMap((token: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);
          const clonedReq = request.clone({ withCredentials: true });
          clonedReq.context.set(IS_RETRIED, true);
          return next.handle(clonedReq);
        }),
        catchError((err) => {
          this.isRefreshing = false;
          console.error('Token refresh failed:', err);
          this.logout();
          return throwError(() => err);
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          const clonedReq = request.clone({ withCredentials: true });
          clonedReq.context.set(IS_RETRIED, true);
          return next.handle(clonedReq);
        }),
      );
    }
  }

  logout(): void {
    this.loginService.logout().subscribe((res) => {
      this.authGuard.logout();
    });
  }
}
